import clsx from 'clsx';
import { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Button from 'src/components/Button';
import Card from 'src/components/Card';
import Checkbox from 'src/components/Checkbox';
import Loader from 'src/components/Loader';
import ProgressIndicator from 'src/components/ProgressIndicator';
import Tag from 'src/components/Tag';
import {
  useSessionActivityQuery,
  useSessionOutcomeQuery,
  useSessionParticipationQuery,
} from 'src/dictionaries/dictionariesApi';
import { useBreakpoint } from 'src/hooks/useBreakpoint';
import useExportToPDF from 'src/hooks/useExportToPDF';
import useTimeFormat from 'src/hooks/useTimeFormat';
import { useUserType } from 'src/hooks/useUserType';
import { IconCalendar, IconChevronLeft, IconLocation, IconTime } from 'src/icons';
import { buildName } from 'src/logic/buildName';
import { formatTime } from 'src/logic/formatTime';
import { getPeriodLabel } from 'src/logic/getPeriodLabel';
import { useAppDispatch } from 'src/store';

import InvoicesSignatureModal, {
  InvoicesSignatureModalRefProps,
} from '../principal/InvoicesSignatureModal';
import StudentHeaderLoading from '../single-student/student-header/StudentHeaderLoading';
import StudentHeaderProfile from '../single-student/student-header/StudentHeaderProfile';
import { BillingActivityField } from './BillingActivityField';
import { invalidateBillingTags, useInvoiceStatementQuery } from './billingApi';
import BillingInvoiceHeaderForPdfExport from './BillingInvoiceHeaderForPdfExport';
import { RejectInvoiceModal } from './RejectInvoiceModal';
type BillingInvoiceSummaryDetailsProps = {
  backTo: string;
  showGoals?: boolean;
  showDownloadBtn?: boolean;
  onDownload?: (id: string) => void;
  showProvider?: boolean;
  showSignatureRequestedAndDueDate?: boolean;
};
const BillingInvoiceSummaryDetails = ({
  showGoals,
  showProvider,
  showSignatureRequestedAndDueDate,
  showDownloadBtn = true,
}: BillingInvoiceSummaryDetailsProps) => {
  const { formatDate, formatDateOnly } = useTimeFormat();
  const [isExportProcessing, setIsExportProcessing] = useState(false);
  const [isRejectMode, setIsRejectMode] = useState(false);
  const [rejectedSessions, setRejectedSessions] = useState<Set<string>>(new Set());
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const { id } = useParams();
  const contentRef = useRef<HTMLDivElement>(null);
  const signatureModalRef = useRef<InvoicesSignatureModalRefProps>(null);
  const exportToPDF = useExportToPDF();
  const navigate = useNavigate();
  const { isFetching, data } = useInvoiceStatementQuery(id!);
  const { isMd } = useBreakpoint('md');
  const { isLg } = useBreakpoint('lg');
  const { isSm } = useBreakpoint('sm');
  const dispatch = useAppDispatch();
  const { userType } = useUserType();
  const { data: sessionActivity } = useSessionActivityQuery();
  const { data: sessionParticipation } = useSessionParticipationQuery();
  const { data: sessionOutcome } = useSessionOutcomeQuery();

  const statement = data;

  const onExportRequest = async () => {
    if (contentRef.current) {
      const element = contentRef.current.parentElement;
      if (element) {
        element.style.visibility = 'visible';

        await new Promise((resolve) => setTimeout(resolve, 100));

        try {
          await exportToPDF(contentRef, `${buildName(statement?.student)}-billing-invoices.pdf`, {
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4',
            imgWidth: 210,
          });
        } finally {
          element.style.visibility = 'hidden';
          setIsExportProcessing(false);
        }
      }
    }
  };

  const onExport = () => {
    setIsExportProcessing(true);
    setTimeout(() => {
      onExportRequest();
    }, 1000);
  };

  const onSignInvoice = () => {
    if (id && signatureModalRef.current) {
      signatureModalRef.current.open({ ids: [id] });
    }
  };

  const onRejectInvoice = () => {
    setIsRejectMode(true);
  };

  const onRejectSessions = () => {
    setIsRejectModalOpen(true);
  };

  const onCancelReject = () => {
    setIsRejectMode(false);
    setRejectedSessions(new Set());
  };

  const onSessionToggle = (sessionId: string, checked: boolean) => {
    const newRejectedSessions = new Set(rejectedSessions);
    if (checked) {
      newRejectedSessions.add(sessionId);
    } else {
      newRejectedSessions.delete(sessionId);
    }
    setRejectedSessions(newRejectedSessions);
  };

  const handleRejectSuccess = () => {
    setIsRejectModalOpen(false);
    setIsRejectMode(false);
    setRejectedSessions(new Set());
    handleSignSuccess();
  };

  const handleRejectClose = () => {
    setIsRejectModalOpen(false);
  };

  const handleSignSuccess = () => {
    dispatch(invalidateBillingTags([{ type: 'InvoiceDetails', id }]));
    navigate(-1);
  };

  if (isFetching || !statement) {
    return <Loader className="my-60 w-full" />;
  }

  const SessionsElement = (
    <div className="mdp-6 flex h-full flex-col justify-start gap-4 border-t border-neutral-800 p-4 md:border-none">
      {statement.sessions.map((session) => {
        return (
          <Card
            key={session.id}
            data-card="session-card"
            titleClassName={clsx(statement.provider ? '' : ' pb-4 md:pb-5')}
            title={
              <div className="grid grid-cols-1 md:grid-cols-3">
                <div className="flex items-center gap-4">
                  {isRejectMode && (
                    <Checkbox
                      isChecked={rejectedSessions.has(session.id)}
                      onCheckedChange={(checked) => onSessionToggle(session.id, checked)}
                      className="!h-5 !w-5"
                    />
                  )}
                  <div className="flex items-center gap-1">
                    <IconCalendar />
                    <span
                      className={clsx(
                        'text-neutral-100',
                        session.isCancelled && statement.student.isSeit && 'line-through',
                      )}
                    >
                      {formatDateOnly(session.date)}
                    </span>
                  </div>
                </div>

                {!isMd ? (
                  <div className="mt-3 flex w-full justify-between">
                    <div className="flex items-center gap-1">
                      <IconTime />
                      <span
                        className={clsx(
                          'text-neutral-100 typography-body-sm',
                          session.isCancelled && statement.student.isSeit && 'line-through',
                        )}
                      >
                        {formatTime(session.startTime)} - {formatTime(session.finishTime)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <IconLocation />
                      <span
                        className={clsx(
                          'text-neutral-100',
                          session.isCancelled && statement.student.isSeit && 'line-through',
                        )}
                      >
                        {session.location}
                      </span>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-1">
                      <IconTime />
                      <span
                        className={clsx(
                          'text-neutral-100 typography-body-sm',
                          session.isCancelled && statement.student.isSeit && 'line-through',
                        )}
                      >
                        {formatTime(session.startTime)} - {formatTime(session.finishTime)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <IconLocation />
                      <span
                        className={clsx(
                          'text-neutral-100',
                          session.isCancelled && statement.student.isSeit && 'line-through',
                        )}
                      >
                        {session.location}
                      </span>
                    </div>
                  </>
                )}
              </div>
            }
          >
            {session.isCancelled && statement.student.isSeit && session.cancellationReason && (
              <div className="border-t border-neutral-900 pt-4">
                <span className="text-red-800 font-medium typography-body-sm">
                  Session canceled:{' '}
                </span>
                <span className="text-red-700 typography-body-sm">
                  {session.cancellationReason}
                </span>
              </div>
            )}
            {showGoals &&
              session.goals.map((goal) => {
                return (
                  <div
                    key={goal.id}
                    className="mt-4 flex w-full flex-col gap-4 border-t border-neutral-900 pt-4"
                  >
                    <span className="text-neutral-100 typography-loud-sm">{goal.name}</span>
                    {statement.student.isSeit ? (
                      <div className="flex flex-col gap-4 xl:flex-row  xl:gap-12">
                        <BillingActivityField
                          type="activity"
                          value={goal.activity?.map(
                            (a) => sessionActivity?.find((s) => s.key === a)?.value || '',
                          )}
                        />
                        <BillingActivityField
                          type="participation"
                          value={
                            sessionParticipation?.find((s) => s.key === goal.participation)
                              ?.value || ''
                          }
                        />
                        <BillingActivityField
                          type="outcome"
                          value={sessionOutcome?.find((s) => s.key === goal.outcome)?.value || ''}
                        />
                      </div>
                    ) : (
                      <div className="flex justify-end">
                        <ProgressIndicator max={5} value={goal.progress} disabled />
                      </div>
                    )}
                  </div>
                );
              })}
          </Card>
        );
      })}
    </div>
  );

  return (
    <div className={`flex min-h-full flex-col bg-neutral-950 ${isRejectMode ? 'pt-4' : ''}`}>
      {showDownloadBtn && !isLg && (
        <div className="fixed bottom-0 left-0 w-full border-t border-neutral-800 bg-neutral-white p-6 sm:absolute">
          <Button
            className="lgv:mt-0 w-full lg:absolute lg:right-0 lg:top-0 lg:w-fit"
            size="md"
            onClick={onExport}
            preset="secondary"
            disabled={isExportProcessing}
            isLoading={isExportProcessing}
          >
            Download Invoice
          </Button>
        </div>
      )}

      <div
        className={`h-auto w-full bg-neutral-white px-4 md:px-10 ${
          isRejectMode ? 'py-0' : 'pb-6  md:pb-10 md:pt-10'
        }`}
      >
        {!isRejectMode && (
          <>
            <Button
              preset="ghost"
              Icon={IconChevronLeft}
              onClick={() => navigate(-1)}
              className="mb-6 mt-4 inline-flex h-auto px-0 md:h-12"
            >
              Back to Invoices
            </Button>

            {showProvider && (
              <div className="mb-6 flex items-center gap-4 border-b border-b-neutral-800 pb-6">
                <span className="typography-heading-md">{buildName(statement.provider)}</span>
                <Tag>Provider</Tag>
              </div>
            )}
          </>
        )}

        <div className="relative">
          {!statement.student ? (
            <StudentHeaderLoading />
          ) : (
            <>
              {!isRejectMode && (
                <div className="flex flex-col md:flex-row md:justify-between">
                  <div className="flex flex-col md:flex-row">
                    <StudentHeaderProfile
                      size="md"
                      student={{ ...statement.student, englishGrade: statement.student.grade }}
                      className="mb-4 mr-6 md:mb-0"
                    />
                    <div className="flex flex-col content-between justify-between gap-2 md:gap-0">
                      {showSignatureRequestedAndDueDate ? (
                        <div className="flex gap-4 overflow-auto">
                          {statement.requestAt && (
                            <Tag size={isMd ? 'md' : 'sm'} preset="yellow">
                              Signature requested: {formatDate(statement.requestAt)}
                            </Tag>
                          )}
                          {statement.dueDate && (
                            <Tag size={isMd ? 'md' : 'sm'} preset="gray">
                              Invoice due date: {formatDate(statement.dueDate)}
                            </Tag>
                          )}
                        </div>
                      ) : (
                        <div className="flex gap-4">
                          <Tag
                            size={isMd ? 'md' : 'sm'}
                            preset={
                              statement.status === 'Pending'
                                ? 'yellow'
                                : statement.status === 'Submitted'
                                ? 'lightGreen'
                                : 'pacific'
                            }
                          >
                            {statement.status}: {formatDate(statement.statusChangedAt)}
                          </Tag>
                          {statement.amount && statement.status === 'Paid' && (
                            <Tag size={isSm ? 'md' : 'sm'} preset="grayStroke">
                              {new Intl.NumberFormat('en-US').format(statement.amount.value)}{' '}
                              {statement.amount.currency}
                            </Tag>
                          )}
                        </div>
                      )}
                      <div className="flex gap-4">
                        <Tag size={isMd ? 'md' : 'sm'} preset="grayStroke">
                          {getPeriodLabel(statement.month, statement.year, statement.period)}
                        </Tag>
                        {(statement.hours || statement.hours === 0) && (
                          <Tag size={isMd ? 'md' : 'sm'} preset="lightBlue">
                            {statement.hours} hours
                          </Tag>
                        )}

                        <Tag size={isMd ? 'md' : 'sm'} preset="grayStroke">
                          {statement.student.serviceType}
                        </Tag>
                      </div>
                    </div>
                  </div>
                  {showSignatureRequestedAndDueDate &&
                    statement.student.isSeit &&
                    userType === 'Principal' && (
                      <div className="mt-4 flex w-full justify-end gap-2 md:ml-auto md:mt-0 md:w-auto">
                        <Button
                          className="flex-1 md:flex-none"
                          preset="tertiary"
                          onClick={onRejectInvoice}
                        >
                          Reject Invoice
                        </Button>
                        <Button
                          className="flex-1 md:flex-none"
                          preset="secondary"
                          onClick={onSignInvoice}
                        >
                          Sign Invoice
                        </Button>
                      </div>
                    )}
                </div>
              )}
            </>
          )}

          {showDownloadBtn && isLg && (
            <Button
              className="mt-4 w-full lg:absolute lg:right-0 lg:top-0 lg:mt-0 lg:w-fit"
              size={isMd ? 'md' : 'xs'}
              onClick={onExport}
              preset="secondary"
              disabled={isExportProcessing}
              isLoading={isExportProcessing}
            >
              Download Invoice
            </Button>
          )}
        </div>
      </div>
      {isRejectMode && (
        <div className={`bg-neutral-950 px-4 py-4 ${isRejectMode ? 'md:px-4' : 'md:px-10'} `}>
          <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
            <div className="text-neutral-300 typography-loud-lg">
              Rejected sessions: {rejectedSessions.size}
            </div>
            <div className="flex flex-col gap-2 md:flex-row">
              <Button
                className="md:flex-none"
                preset="secondary"
                onClick={onRejectSessions}
                disabled={rejectedSessions.size === 0}
              >
                Reject Sessions
              </Button>
              <Button className="md:flex-none" preset="tertiary" onClick={onCancelReject}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
      {isExportProcessing && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: '-5000px',
            width: '210mm',
            visibility: 'hidden',
            zIndex: -9999,
          }}
          className="bg-white"
        >
          <div
            className="bg-white w-full p-6"
            style={{
              minHeight: 'auto',
              height: 'auto',
              overflow: 'visible',
            }}
            ref={contentRef}
          >
            <BillingInvoiceHeaderForPdfExport
              size="md"
              student={{ ...statement.student, englishGrade: statement.student.grade }}
              statement={statement}
            />
            {SessionsElement}
          </div>
        </div>
      )}
      {SessionsElement}
      <RejectInvoiceModal
        isOpen={isRejectModalOpen}
        onClose={handleRejectClose}
        invoiceId={id!}
        rejectedSessions={rejectedSessions}
        onSuccess={handleRejectSuccess}
      />
      <InvoicesSignatureModal ref={signatureModalRef} onSuccess={handleSignSuccess} />
    </div>
  );
};

export default BillingInvoiceSummaryDetails;
